import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import util from "../../utils/miniUtils";
import { showApiError } from "./salesHelpers";

// INVOICE_MAX_FILE_BYTES de inventory-api
const MAX_FILE_BYTES = 10 * 1024 * 1024;

// La API vuelve a revisar extensión, tipo y contenido: esto solo evita subir un archivo equivocado
const fileSchema = ( label, extension ) => Yup.mixed()
    .required( `El archivo ${label} es obligatorio` )
    .test( "extension", `El archivo ${label} debe ser ${extension}`, ( file ) => !file || file.name.toLowerCase().endsWith( extension ) )
    .test( "size", `El archivo ${label} no puede pasar de 10 MB`, ( file ) => !file || file.size <= MAX_FILE_BYTES );

const validationSchema = Yup.object().shape( {
    pdf: fileSchema( "PDF", ".pdf" ),
    xml: fileSchema( "XML", ".xml" ),
} );

// Subir el PDF y el XML de la factura del pedido. Al confirmar la orden queda facturada y el cliente recibe un correo.
export default function InvoiceUploadForm( { order, onSaved } ) {
    const formik = useFormik( {
        initialValues: { pdf: null, xml: null },
        validationSchema,
        onSubmit: uploadInvoice,
    } );

    async function uploadInvoice( values ) {
        const confirmation = await Swal.fire( {
            title: "¿Cargar la factura?",
            text: "La orden quedará como facturada y le avisaremos al cliente por correo.",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Cargar factura",
            cancelButtonText: "Cancelar",
        } );
        if ( !confirmation.isConfirmed ) return;

        try {
            const body = new FormData();
            body.append( "pdf", values.pdf );
            body.append( "xml", values.xml );
            const request = await util.axios.post( `sales/orders/${order.uuid}/invoice`, body );
            const { error, msg } = request.data;
            if ( error ) throw msg;
            util.toast().success( msg );
            onSaved();
        } catch ( e ) {
            showApiError( e );
        }
    }

    const handleFile = ( event ) => formik.setFieldValue( event.target.name, event.target.files[0] || null );
    const fieldError = ( name ) => ( formik.touched[name] || formik.submitCount > 0 ) && formik.errors[name];

    return (
        <form onSubmit={formik.handleSubmit} className="mt-3" data-cy="sales-invoice-form">
            <div className="mb-2">
                <label htmlFor="sales-invoice-pdf" className="form-label small mb-0">Factura en PDF</label>
                <input id="sales-invoice-pdf" name="pdf" type="file" accept=".pdf,application/pdf" className="form-control form-control-sm"
                    onChange={handleFile} onBlur={formik.handleBlur} data-cy="sales-invoice-pdf" />
                {fieldError( "pdf" ) && <div className="text-danger small">{formik.errors.pdf}</div>}
            </div>
            <div className="mb-2">
                <label htmlFor="sales-invoice-xml" className="form-label small mb-0">Factura en XML</label>
                <input id="sales-invoice-xml" name="xml" type="file" accept=".xml,application/xml,text/xml" className="form-control form-control-sm"
                    onChange={handleFile} onBlur={formik.handleBlur} data-cy="sales-invoice-xml" />
                {fieldError( "xml" ) && <div className="text-danger small">{formik.errors.xml}</div>}
            </div>
            <button type="submit" className="btn btn-sm btn-primary" disabled={formik.isSubmitting} data-cy="sales-invoice-submit">
                {formik.isSubmitting ? "Cargando…" : "Cargar factura"}
            </button>
        </form>
    );
}
